import React from 'react';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import { RecordActionsMenu } from '@/shared/components/RecordActionsMenu';
import type { RFIPriority, RFIStatus, RFISummary } from '../types';
import { RFI_PRIORITY_LABELS, RFI_STATUS_LABELS } from '../types';

// Solid, filled status pills.
const STATUS_BADGE: Record<RFIStatus, string> = {
  DRAFT: 'border-transparent bg-gray-500 text-white hover:bg-gray-500',
  OPEN: 'border-transparent bg-blue-600 text-white hover:bg-blue-600',
  PENDING_RESPONSE: 'border-transparent bg-amber-500 text-white hover:bg-amber-500',
  ANSWERED: 'border-transparent bg-green-600 text-white hover:bg-green-600',
  CLOSED: 'border-transparent bg-slate-600 text-white hover:bg-slate-600',
};

// Lighter tinted priority pills so status stays the dominant signal.
const PRIORITY_BADGE: Record<RFIPriority, string> = {
  LOW: 'border-transparent bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  NORMAL: 'border-transparent bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
  HIGH: 'border-transparent bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100',
  URGENT: 'border-transparent bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  return format(new Date(iso), 'MMM d, yyyy');
};

const COLUMN_COUNT = 9;

interface Props {
  items: RFISummary[];
  isLoading: boolean;
  error?: unknown;
  onRowClick: (item: RFISummary) => void;
  onEdit: (item: RFISummary) => void;
  onDelete: (item: RFISummary) => void;
}

export const RfisTable: React.FC<Props> = ({
  items,
  isLoading,
  error,
  onRowClick,
  onEdit,
  onDelete,
}) => {
  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: COLUMN_COUNT }).map((__, j) => (
          <TableCell key={j} className="py-2">
            <Skeleton className="h-4 w-full" />
          </TableCell>
        ))}
      </TableRow>
    ));

  return (
    <div className="min-w-0 space-y-2" data-tour-id="rfis-table">
      <div className="space-y-2 md:hidden">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-64 w-full rounded-md" />
          ))
        ) : error ? (
          <div className="rounded-md border px-4 py-8 text-center text-sm text-destructive">
            Failed to load RFIs.
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-md border px-4 py-10 text-center text-sm text-muted-foreground">
            No RFIs found.
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="w-full min-w-0 rounded-md border bg-card p-4 text-left transition-colors hover:bg-muted/50"
              onClick={() => onRowClick(item)}
            >
              <div className="flex min-w-0 items-start gap-3">
                <div className="min-w-0 flex-1">
                  <span className="font-mono text-xs text-muted-foreground">
                    {item.rfi_number}
                  </span>
                  <p className="mt-1 break-words text-sm font-semibold">{item.title}</p>
                </div>
                <RecordActionsMenu
                  label={`RFI ${item.rfi_number}`}
                  canEdit={item.can_edit}
                  canDelete={item.can_delete}
                  onView={() => onRowClick(item)}
                  onEdit={() => onEdit(item)}
                  onDelete={() => onDelete(item)}
                />
              </div>
              <div className="mt-4 grid min-w-0 grid-cols-2 gap-x-4 gap-y-3">
                <MobileCardField label="Status">
                  <Badge className={`max-w-full whitespace-normal text-left text-xs ${STATUS_BADGE[item.status]}`}>
                    {RFI_STATUS_LABELS[item.status]}
                  </Badge>
                </MobileCardField>
                <MobileCardField label="Priority">
                  <Badge className={`max-w-full whitespace-normal text-left text-xs ${PRIORITY_BADGE[item.priority]}`}>
                    {RFI_PRIORITY_LABELS[item.priority]}
                  </Badge>
                </MobileCardField>
                <MobileCardField label="Responsible Party">
                  {item.responsible_party_user
                    ? `${item.responsible_party_user.first_name} ${item.responsible_party_user.last_name}`
                    : 'Unassigned'}
                </MobileCardField>
                <MobileCardField label="Assignee">
                  {item.assignee_user
                    ? `${item.assignee_user.first_name} ${item.assignee_user.last_name}`
                    : 'Unassigned'}
                </MobileCardField>
                <MobileCardField label="Due Date">
                  <span
                    className={
                    item.is_overdue ? 'font-semibold text-red-600' : ''
                    }
                  >
                    {formatDate(item.due_date)}
                  </span>
                </MobileCardField>
                <MobileCardField label="Drawing">
                  {item.drawing_reference?.label || 'No drawing'}
                </MobileCardField>
                <MobileCardField label="Created">
                  {formatDate(item.created_at)}
                </MobileCardField>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-md border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs font-medium whitespace-nowrap">RFI #</TableHead>
              <TableHead className="text-xs font-medium">Title</TableHead>
              <TableHead className="text-xs font-medium w-[150px]">Status</TableHead>
              <TableHead className="text-xs font-medium w-[100px]">Priority</TableHead>
              <TableHead className="text-xs font-medium">Responsible Party</TableHead>
              <TableHead className="text-xs font-medium whitespace-nowrap">Due</TableHead>
              <TableHead className="text-xs font-medium">Drawing Ref</TableHead>
              <TableHead className="text-xs font-medium whitespace-nowrap">Created</TableHead>
              <TableHead className="w-[72px] text-xs font-medium">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              renderSkeletonRows()
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="py-8 text-center text-destructive">
                  Failed to load RFIs.
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="py-12 text-center text-sm text-muted-foreground">
                  No RFIs found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <RfiRow
                  key={item.id}
                  item={item}
                  onView={onRowClick}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

function MobileCardField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 min-w-0 break-words text-xs font-medium text-foreground">
        {children}
      </div>
    </div>
  );
}

function RfiRow({
  item,
  onView,
  onEdit,
  onDelete,
}: {
  item: RFISummary;
  onView: (item: RFISummary) => void;
  onEdit: (item: RFISummary) => void;
  onDelete: (item: RFISummary) => void;
}) {
  return (
    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => onView(item)}>
      <TableCell className="py-1.5 text-xs font-mono text-muted-foreground whitespace-nowrap">
        {item.rfi_number}
      </TableCell>
      <TableCell className="max-w-[240px] truncate py-1.5 text-xs font-medium">
        {item.title}
      </TableCell>
      <TableCell className="py-1.5">
        <Badge className={`text-xs whitespace-nowrap ${STATUS_BADGE[item.status]}`}>
          {RFI_STATUS_LABELS[item.status]}
        </Badge>
      </TableCell>
      <TableCell className="py-1.5">
        <Badge className={`text-xs whitespace-nowrap ${PRIORITY_BADGE[item.priority]}`}>
          {RFI_PRIORITY_LABELS[item.priority]}
        </Badge>
      </TableCell>
      <TableCell className="py-1.5 text-xs text-muted-foreground">
        {item.responsible_party_user
          ? `${item.responsible_party_user.first_name} ${item.responsible_party_user.last_name}`
          : '—'}
      </TableCell>
      <TableCell
        className={`py-1.5 text-xs whitespace-nowrap ${
          item.is_overdue ? 'font-semibold text-red-600' : 'text-muted-foreground'
        }`}
      >
        {formatDate(item.due_date)}
      </TableCell>
      <TableCell className="py-1.5 text-xs text-muted-foreground">
        {item.drawing_reference?.label || '—'}
      </TableCell>
      <TableCell className="py-1.5 text-xs whitespace-nowrap text-muted-foreground">
        {formatDate(item.created_at)}
      </TableCell>
      <TableCell className="py-1.5">
        <RecordActionsMenu
          label={`RFI ${item.rfi_number}`}
          canEdit={item.can_edit}
          canDelete={item.can_delete}
          onView={() => onView(item)}
          onEdit={() => onEdit(item)}
          onDelete={() => onDelete(item)}
        />
      </TableCell>
    </TableRow>
  );
}

export default RfisTable;
